import React, { useRef, memo, useEffect } from 'react';
import { Card, Form, Radio, Space, Spin, Typography, Button, Alert, message } from 'antd';
import { useDispatch } from 'react-redux';
import useProviderEditDisabled from 'hooks/useProviderEditDisabled';
import { ADD_NAVY_HCC_STEPS, ADD_AIR_FORCE_HCC_STEPS } from 'constants';
import providerService from 'services/providers.service';
import { actions as centerActions } from 'features/add-center';
import { sleep } from 'utils';
import { FormItemLabel } from 'components/Form';
import { PROVIDER_APPLICATION_PROGRAM_TYPES } from 'containers/Provider/SelectProgram/SelectProgram';

const { Title } = Typography;

const HCCHoursOfOperation = ({
  id,
  application,
  setApplication,
  setStep,
  isFormSubmitting,
  loading,
  applicationSubmitted,
}) => {
  const dispatch = useDispatch();
  const isSubmitDisabled = useProviderEditDisabled();
  const isAirForce = application?.programType.id === PROVIDER_APPLICATION_PROGRAM_TYPES.AIR_FORCE_HCC.id;

  const [form] = Form.useForm();
  const formHccEligibleValue = Form.useWatch('hccEligible', form);

  // Keeps track of whether `next` is pressed or `save`
  const shouldGoToNextStep = useRef(false);

  useEffect(() => {
    form.setFieldsValue({
      hccEligible: application?.hccEligible,
    });
  }, [application, form]);

  return (
    <div id="hours-of-operation" data-testid="hoursOfOperation">
      <Spin spinning={loading}>
        <Form
          layout="vertical"
          form={form}
          scrollToFirstError
          onFinish={async (values) => {
            try {
              dispatch(centerActions.setIsFormSubmitting(true));

              // update `hccEligible` in the application before submitting
              const updatedApplication = await providerService.updateApplication(id, {
                ...application,
                hccEligible: values.hccEligible,
              });
              setApplication(updatedApplication);

              if (shouldGoToNextStep.current) {
                setStep(ADD_NAVY_HCC_STEPS.STAFF_MEMBERS);
              }

              message.success('Saved');
              await sleep(100);
            } catch (error) {
              newrelic.noticeError(error);
              message.error('Unable to save center details');
            } finally {
              dispatch(centerActions.setIsFormSubmitting(false));
            }
          }}
          disabled={applicationSubmitted}
        >
          <Title>Hours of operation</Title>
          <Card className="mb-4">
            <Form.Item
              name="hccEligible"
              label={
                <FormItemLabel
                  title="does this facility offer services on saturdays and sundays from 6:00am to 6:00pm?"
                  subTitle={`The facility must provide services from 6:00am to 6:00pm on Saturdays and Sundays in order to
                    qualify for assistance under the ${isAirForce ? 'Air Force' : 'Navy'} Home Community Care program.`}
                />
              }
              rules={[{ required: true, message: 'Selection is required' }]}
              className="mb-0"
            >
              <Radio.Group className="mt-1" data-testid="operatesOnWeekends">
                <Space direction="vertical">
                  <Radio value={true} data-testid="yesRadio">
                    Yes
                  </Radio>
                  <Radio value={false} data-testid="noRadio">
                    No
                  </Radio>
                </Space>
              </Radio.Group>
            </Form.Item>

            {formHccEligibleValue === false && (
              <Alert
                message={`You may not qualify for the ${
                  isAirForce ? 'Air Force' : 'Navy'
                } Home Community Care program. You must offer services Saturday and Sunday 6:00am-6:00pm in order to qualify.`}
                type="warning"
                className="mt-4"
              />
            )}
          </Card>

          <div className="flex space-x-2">
            <Button
              onClick={() =>
                setStep(isAirForce ? ADD_AIR_FORCE_HCC_STEPS.CREDENTIALS : ADD_NAVY_HCC_STEPS.PROGRAM_INFO)
              }
              style={{ borderRadius: '0px' }}
              aria-label="back button"
              disabled={false}
              data-testid="backButton"
            >
              Back
            </Button>

            <div className="flex-grow"></div>

            {!applicationSubmitted && (
              <Button
                style={{ borderRadius: '0px' }}
                htmlType="submit"
                onClick={() => {
                  shouldGoToNextStep.current = false;
                }}
                disabled={isFormSubmitting || isSubmitDisabled}
                loading={isFormSubmitting}
                aria-label="save button"
              >
                Save
              </Button>
            )}

            <Button
              style={{ borderRadius: '0px' }}
              type="primary"
              role="button"
              htmlType={!applicationSubmitted ? 'submit' : undefined}
              onClick={() => {
                if (applicationSubmitted) {
                  setStep(ADD_NAVY_HCC_STEPS.STAFF_MEMBERS);
                  return;
                }
                shouldGoToNextStep.current = true;
              }}
              disabled={isFormSubmitting || isSubmitDisabled}
              loading={isFormSubmitting}
              data-testid="hours-of-operation-form-submit"
              aria-label="next button"
            >
              Next
            </Button>
          </div>
        </Form>
      </Spin>
    </div>
  );
};

export default memo(HCCHoursOfOperation);
